/**
 * Metro pricing matrix — extracts every metro mentioned in windowDrivers.js
 * and computes typical 2026 cost ranges for all 11 calculator types using:
 *
 *   metro_cost(calc) = calc_baseline × state_index × metro_modifier
 *
 * The metro_modifier is derived from the curated window-replacement metro
 * pricing in `windowDrivers.js`. For each metro it computes:
 *
 *   metro_window_midpoint  = (low + high) / 2
 *   state_window_avg       = avg of all metro midpoints for that state
 *   metro_modifier         = metro_window_midpoint / state_window_avg
 *
 * That gives each metro a real-data-derived premium / discount factor
 * vs. its state average. We then apply that factor to each calculator's
 * national baseline (multiplied by the state's overall cost index) to
 * estimate metro-level project costs.
 *
 * Honest framing: window data is metro-curated; other calculator pricing
 * is modeled. The UI surfaces this distinction. Modeling is far better
 * than nothing — and the metro_modifier captures real labor + materials
 * variance that pure state-level averages miss (Manhattan vs. Buffalo
 * have a 1.8x window-cost gap inside a state with one index value).
 *
 * NOTE: this file is distinct from `metros.js`, which carries hand-curated
 * per-state labor-cost notes used inline on State Calculator pages.
 */
import { WINDOW_DRIVERS } from "@/lib/windowDrivers";
import { US_STATES } from "@/lib/states";
import { getStateCostIndex } from "@/lib/stateDrivers";

// National baseline typical project cost (mid-tier, ~2,000 sqft home, 2026)
// for each of the 11 calculator types. Derived from /lib/calculators.js
// "typical project" ranges and Cost-vs-Value Report 2026 mid-tier figures.
export const NATIONAL_BASELINES = {
  bathroom: { low: 14000, high: 32000, label: "Bathroom remodel", short: "Bathroom" },
  kitchen: { low: 25000, high: 60000, label: "Kitchen remodel", short: "Kitchen" },
  flooring: { low: 8000, high: 24000, label: "Whole-home flooring", short: "Flooring" },
  roofing: { low: 11000, high: 22000, label: "Roof replacement", short: "Roofing" },
  deck: { low: 9000, high: 28000, label: "Deck addition (composite)", short: "Deck" },
  basement: { low: 30000, high: 65000, label: "Basement finishing", short: "Basement" },
  windows: { low: 14000, high: 28000, label: "Window replacement (~15)", short: "Windows" },
  solar: { low: 14000, high: 22000, label: "Solar (8 kW)", short: "Solar" },
  fence: { low: 4000, high: 12000, label: "Fence install (150 ft)", short: "Fence" },
  pool: { low: 42000, high: 95000, label: "Pool installation (inground)", short: "Pool" },
  painting: { low: 4000, high: 9000, label: "Whole-home exterior paint", short: "Painting" },
};

// State code → URL slug used to link to /{calc}-cost-{slug} pages.
const STATE_SLUG = US_STATES.reduce(
  (acc, s) => {
    acc[s.code] = s.slug;
    return acc;
  },
  {},
);

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/\(.*?\)/g, "") // drop parenthetical notes "(HVHZ)" before slugging
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function buildMetroIndex() {
  const out = [];
  for (const stateCode of Object.keys(WINDOW_DRIVERS)) {
    const profile = WINDOW_DRIVERS[stateCode];
    if (!profile.metros || profile.metros.length === 0) continue;
    const stateMeta = US_STATES.find((s) => s.code === stateCode);
    if (!stateMeta) continue;
    // Compute state's average window cost across its metros — used as the
    // denominator in the metro modifier.
    const stateAvg =
      profile.metros.reduce((acc, m) => acc + (m.low + m.high) / 2, 0) /
      profile.metros.length;
    for (const m of profile.metros) {
      const mid = (m.low + m.high) / 2;
      // Clean parenthetical descriptors out of the slug source, keep them
      // visible in displayCity for UI clarity ("Miami-Dade (HVHZ)").
      const cleanCity = m.city.replace(/\s*\(.*?\)\s*/g, "").trim();
      out.push({
        city: cleanCity,
        displayCity: m.city,
        state: stateMeta.name,
        stateCode,
        stateSlug: STATE_SLUG[stateCode] || "",
        slug: `${slugify(cleanCity)}-${stateMeta.slug}`,
        windowLow: m.low,
        windowHigh: m.high,
        windowMid: mid,
        // metro_modifier: this metro's window cost relative to its state's
        // average. ~1.0 = state-average; >1.0 = more expensive sub-region;
        // <1.0 = cheaper sub-region. Clamped to [0.6, 1.8] to keep extreme
        // outliers (Manhattan, Aspen, Honolulu) from producing absurd
        // derived figures on calculators that don't scale the same way.
        modifier: Math.max(0.6, Math.min(1.8, mid / stateAvg)),
      });
    }
  }
  // Sort by state name then city for stable rendering.
  out.sort((a, b) => {
    if (a.state !== b.state) return a.state.localeCompare(b.state);
    return a.city.localeCompare(b.city);
  });
  return out;
}

let _METROS = null;
export function getAllMetros() {
  if (!_METROS) _METROS = buildMetroIndex();
  return _METROS;
}

/**
 * Compute the typical project cost band for any calculator in a metro.
 * For the windows calculator, returns the curated metro data directly
 * (windowLow/windowHigh from windowDrivers.js).
 */
export function getMetroCalcPricing(metro, calcSlug) {
  if (calcSlug === "windows") {
    // metro.windowLow / windowHigh are SINGLE-window installed costs from the
    // curated metro data. Scale up to a whole-home job (~15 windows) so this
    // row is apples-to-apples with the other 10 calculator rows which all
    // express typical whole-project totals.
    return {
      low: Math.round((metro.windowLow * 15) / 100) * 100,
      high: Math.round((metro.windowHigh * 15) / 100) * 100,
    };
  }
  const baseline = NATIONAL_BASELINES[calcSlug];
  if (!baseline) return null;
  const stateIndex = getStateCostIndex(metro.stateCode);
  const factor = stateIndex * metro.modifier;
  return {
    low: Math.round((baseline.low * factor) / 100) * 100,
    high: Math.round((baseline.high * factor) / 100) * 100,
  };
}

export function getMetrosByState() {
  const metros = getAllMetros();
  const map = new Map();
  for (const m of metros) {
    if (!map.has(m.stateCode)) {
      map.set(m.stateCode, {
        state: m.state,
        stateCode: m.stateCode,
        stateSlug: m.stateSlug,
        metros: [],
      });
    }
    map.get(m.stateCode).metros.push(m);
  }
  // Return states sorted alphabetically by name.
  return Array.from(map.values()).sort((a, b) => a.state.localeCompare(b.state));
}
